import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { vacancyFormActions } from '../../redux/slices/vacancyFormSlice';
import { useAppDispatch, useAppSelector, useHideWebsiteFooter, useIsMobile } from '../../../../../base/hooks';
import { __t } from '../../../../../base/utils/t';

import { Helmet } from '../../../../components/Helmet';
import { Header } from '../../../../../base/components/Header';
import { Step1 } from '../../components/Step1';
import { Step2 } from '../../components/Step2';
import { Step3 } from '../../components/Step3';
import { Step4 } from '../../components/Step4';
import { PreviewModal } from '../../components/PreviewModal';
import { Footer } from '../../components/Footer';

import './styles.scss';

interface Props {
    duplicate?: boolean;
}

export const VacancyForm = ({ duplicate }: Props) => {
    const { id } = useParams();
    const {
        vacancyForm: { step },
        app: { referrer },
    } = useAppSelector((state) => state);
    const dispatch = useAppDispatch();
    const isMobile = useIsMobile();
    useHideWebsiteFooter();

    const [showPreview, setShowPreview] = useState(false);

    useEffect(() => {
        return () => {
            dispatch(vacancyFormActions.refreshVacancyDataToInitialState());
        };
    }, []);

    useEffect(() => {
        dispatch(vacancyFormActions.init(id));
        if (duplicate) {
            dispatch(vacancyFormActions.setDuplicate(duplicate));
        }
    }, [id, duplicate]);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, [step]);

    useEffect(() => {
        if (isMobile) {
            window.history.pushState(null, '', window.location.href);
            window.onpopstate = function () {
                window.history.go(1);
                if (step === 1) {
                    if (referrer) {
                        window.location.href = referrer;
                        return;
                    }
                    return;
                }

                dispatch(vacancyFormActions.setStep(step - 1));
            };
        }

        return () => {
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            window.onpopstate = () => {};
        };
    }, [isMobile, step, referrer]);

    const onBackClick = useCallback(() => {
        if (step === 1) {
            if (referrer) {
                window.location.href = referrer;
                return;
            }
            return;
        }

        dispatch(vacancyFormActions.setStep(step - 1));
    }, [step, referrer]);

    const togglePreviewVisibility = () => setShowPreview(!showPreview);

    const pageTitle = useMemo(() => {
        if (window.GLOBALS.vacancyId && !window.GLOBALS.duplicate) {
            return __t('Редактирование вакансии');
        }
        return __t('Новая вакансия');
    }, []);

    const stepContent = useMemo(() => {
        switch (step) {
            case 1:
                return <Step1 />;
            case 2:
                return <Step2 />;
            case 3:
                return <Step3 />;
            case 4:
                return (
                    <>
                        <Step4 previewClickHandler={togglePreviewVisibility} />
                        <PreviewModal visible={showPreview} visibilityHandler={togglePreviewVisibility} />
                    </>
                );
            default:
                return null;
        }
    }, [step, showPreview]);

    const pageName = useMemo(() => {
        if (duplicate) {
            return __t('Копирование вакансии');
        } else if (id) {
            return __t('Редактирование вакансии');
        } else {
            return __t('Новая вакансия');
        }
    }, [duplicate, id]);

    return (
        <>
            <Helmet pageName={pageName} />
            <div className='vacancy-form-page'>
                <Header title={pageTitle} onBackClick={onBackClick} />
                <div className='vacancy-form-page__form'>{stepContent}</div>
                <Footer previewClickHandler={togglePreviewVisibility} />
            </div>
        </>
    );
};
